<template>
    <!--
        Nakliyeci Hakediş Raporu (Task 5)

        Müşterinin Excel pivotunun (nakliyeci → dönem → grup → malzeme) dijital karşılığı.
        Kaynak veri: IrsaliyeGroup + IrsaliyeGroupSefer (Task 2a sonrası CounterTonnage kolonu
        varlığı sayesinde "Vişne / Müşteri / Fark" ayrımı yapabiliyoruz).

        - Üstte filtre: tarih aralığı + nakliyeci + şube
        - 4 KPI kartı: Toplam sefer, Vişne tonaj, Müşteri tonaj, Fark, Toplam hakediş
        - İki grafik: Ürün dağılımı (donut) + Günlük trend (line)
        - Kırılım tablosu: nakliyeci genişletilebilir → dönem → grup → malzeme
        - Excel'e Aktar: muhasebe akışı için (adam mail'de "excele ihtiyaç duymadan" dedi ama
          ilk sürümde Excel'i öldürmüyoruz, paralel tutuyoruz)
    -->
    <b-card>
        <div class="row">
            <div class="col-md-4 mb-1">
                <label class="form-label">Tarih Aralığı</label>
                <v-date-picker v-model="date" range placeholder="Tarih Aralığı"></v-date-picker>
            </div>
            <div class="col-md-4 mb-1">
                <label class="form-label">Nakliyeci <small class="text-muted">(hepsi için boş)</small></label>
                <v-select-list v-model="cari" :options="cariList" label="CARI_ISIM"
                               v-on:search="searchCari" :loading="cariLoading" placeholder="Nakliyeci"></v-select-list>
            </div>
            <div class="col-md-4 mb-1">
                <label class="form-label">Şube <small class="text-muted">(hepsi için boş)</small></label>
                <v-select-list v-model="sube" :options="subeList" label="BranchName" placeholder="Şube"></v-select-list>
            </div>
        </div>
        <div class="row justify-content-end">
            <div class="col-auto mb-1 mb-md-0">
                <b-button variant="outline-success" size="sm" v-on:click="downloadExcel" :disabled="excelLoading">
                    <feather-icon icon="DownloadIcon" class="mr-25"></feather-icon>
                    {{ excelLoading ? 'Hazırlanıyor...' : 'Excele Aktar' }}
                </b-button>
            </div>
            <div class="col-auto mb-1 mb-md-0">
                <v-executer :loading="loading" :action="loadReport">Rapor Al</v-executer>
            </div>
        </div>

        <hr>

        <!-- KPI kartları -->
        <div class="row mt-2" v-if="rapor">
            <div class="col-md-3 col-6 mb-1">
                <v-stats-box text="Toplam Sefer" variant="primary" icon="local_shipping"
                             :value="String(rapor.Ozet.ToplamSefer || 0)"></v-stats-box>
            </div>
            <div class="col-md-3 col-6 mb-1">
                <v-stats-box text="Vişne Tonaj" variant="secondary" icon="factory"
                             :value="rapor.Ozet.ToplamVisneTonajStr + ' ton'"></v-stats-box>
            </div>
            <div class="col-md-3 col-6 mb-1">
                <v-stats-box :text="'Müşteri Tonaj (Fark: ' + rapor.Ozet.ToplamFarkStr + ')'"
                             :variant="farkVariant"
                             icon="scale"
                             :value="rapor.Ozet.ToplamMusteriTonajStr + ' ton'"></v-stats-box>
            </div>
            <div class="col-md-3 col-6 mb-1">
                <v-stats-box text="Toplam Hakediş" variant="success" icon="payments"
                             :value="rapor.Ozet.ToplamHakedisStr + ' ₺'"></v-stats-box>
            </div>
        </div>

        <!-- Grafikler -->
        <div class="row mt-2" v-if="rapor && rapor.Urunler.length > 0">
            <div class="col-md-5 mb-2">
                <b-card no-body class="p-1">
                    <h4 class="mb-1">Ürün Bazında Hakediş Dağılımı</h4>
                    <vue-apex-charts v-if="donutSeries.length > 0"
                        type="donut" height="300"
                        :options="donutOptions" :series="donutSeries"></vue-apex-charts>
                    <p v-else class="text-muted text-center py-3">Veri yok</p>
                </b-card>
            </div>
            <div class="col-md-7 mb-2">
                <b-card no-body class="p-1">
                    <h4 class="mb-1">Günlük Trend</h4>
                    <vue-apex-charts v-if="rapor.Gunluk.length > 0"
                        type="area" height="300"
                        :options="lineOptions" :series="lineSeries"></vue-apex-charts>
                    <p v-else class="text-muted text-center py-3">Veri yok</p>
                </b-card>
            </div>
        </div>

        <!-- Kırılım tablosu -->
        <div class="row mt-2" v-if="rapor && rapor.Nakliyeciler.length > 0">
            <div class="col-12">
                <h3 class="mb-1">
                    Nakliyeci Kırılımı
                    <b-badge variant="light-secondary" class="ml-1">
                        {{ rapor.Nakliyeciler.length }} nakliyeci · {{ rapor.Ozet.GrupSayisi }} grup
                    </b-badge>
                </h3>
                <div class="table-responsive">
                    <table class="table table-sm table-bordered align-middle">
                        <thead class="bg-light">
                            <tr>
                                <th style="width:34px"></th>
                                <th>Nakliyeci / Dönem / Grup / Malzeme</th>
                                <th class="text-right">Sefer</th>
                                <th class="text-right">Vişne Tonaj</th>
                                <th class="text-right">Müşteri Tonaj</th>
                                <th class="text-right">Fark</th>
                                <th class="text-right">Ort. Birim Fiyat</th>
                                <th class="text-right">Toplam Hakediş (TL)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(nak, ni) in rapor.Nakliyeciler">
                                <!-- Nakliyeci satırı -->
                                <tr :key="'n-' + ni" class="font-weight-bolder" style="background:#f7f7fb; cursor:pointer"
                                    v-on:click="toggle('n-'+ni)">
                                    <td class="text-center">
                                        <feather-icon :icon="isOpen('n-'+ni) ? 'ChevronDownIcon' : 'ChevronRightIcon'"></feather-icon>
                                    </td>
                                    <td>{{ nak.CariName }}</td>
                                    <td class="text-right">{{ nak.SeferSayisi }}</td>
                                    <td class="text-right">{{ nak.VisneTonajStr }}</td>
                                    <td class="text-right">{{ nak.MusteriTonajStr }}</td>
                                    <td class="text-right" :class="farkColor(nak.Fark)">{{ nak.FarkStr }}</td>
                                    <td></td>
                                    <td class="text-right">{{ nak.HakedisStr }}</td>
                                </tr>
                                <!-- Dönemler -->
                                <template v-if="isOpen('n-'+ni)">
                                    <template v-for="(don, di) in nak.Donemler">
                                        <tr :key="'d-'+ni+'-'+di" style="cursor:pointer"
                                            v-on:click="toggle('d-'+ni+'-'+di)">
                                            <td></td>
                                            <td class="pl-3">
                                                <feather-icon :icon="isOpen('d-'+ni+'-'+di) ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="12"></feather-icon>
                                                <span class="ml-25">{{ don.DonemStr }}</span>
                                            </td>
                                            <td class="text-right">{{ don.SeferSayisi }}</td>
                                            <td class="text-right">{{ don.VisneTonajStr }}</td>
                                            <td class="text-right">{{ don.MusteriTonajStr }}</td>
                                            <td class="text-right" :class="farkColor(don.Fark)">{{ don.FarkStr }}</td>
                                            <td></td>
                                            <td class="text-right">{{ don.HakedisStr }}</td>
                                        </tr>
                                        <!-- Gruplar -->
                                        <template v-if="isOpen('d-'+ni+'-'+di)">
                                            <template v-for="(grup, gi) in don.Gruplar">
                                                <tr :key="'g-'+ni+'-'+di+'-'+gi" style="cursor:pointer"
                                                    v-on:click="toggle('g-'+ni+'-'+di+'-'+gi)">
                                                    <td></td>
                                                    <td class="pl-4">
                                                        <feather-icon :icon="isOpen('g-'+ni+'-'+di+'-'+gi) ? 'ChevronDownIcon' : 'ChevronRightIcon'" size="12"></feather-icon>
                                                        <b-badge variant="light-info" class="ml-25">{{ grup.ReferenceNo }}</b-badge>
                                                    </td>
                                                    <td class="text-right">{{ grup.SeferSayisi }}</td>
                                                    <td class="text-right">{{ grup.VisneTonajStr }}</td>
                                                    <td class="text-right">{{ grup.MusteriTonajStr }}</td>
                                                    <td class="text-right" :class="farkColor(grup.Fark)">{{ grup.FarkStr }}</td>
                                                    <td></td>
                                                    <td class="text-right">{{ grup.HakedisStr }}</td>
                                                </tr>
                                                <!-- Malzemeler -->
                                                <template v-if="isOpen('g-'+ni+'-'+di+'-'+gi)">
                                                    <tr v-for="(mal, mi) in grup.Malzemeler" :key="'m-'+ni+'-'+di+'-'+gi+'-'+mi">
                                                        <td></td>
                                                        <td class="pl-5 text-muted">{{ mal.MalzemeAdi }}</td>
                                                        <td class="text-right">{{ mal.SeferSayisi }}</td>
                                                        <td class="text-right">{{ mal.VisneTonajStr }}</td>
                                                        <td class="text-right">{{ mal.MusteriTonajStr }}</td>
                                                        <td class="text-right" :class="farkColor(mal.Fark)">{{ mal.FarkStr }}</td>
                                                        <td class="text-right">{{ mal.OrtBirimFiyatStr || '-' }}</td>
                                                        <td class="text-right">{{ mal.HakedisStr }}</td>
                                                    </tr>
                                                </template>
                                            </template>
                                        </template>
                                    </template>
                                </template>
                            </template>

                            <!-- Genel toplam -->
                            <tr class="font-weight-bolder" style="background:#eef4ff">
                                <td></td>
                                <td>GENEL TOPLAM</td>
                                <td class="text-right">{{ rapor.Ozet.ToplamSefer }}</td>
                                <td class="text-right">{{ rapor.Ozet.ToplamVisneTonajStr }}</td>
                                <td class="text-right">{{ rapor.Ozet.ToplamMusteriTonajStr }}</td>
                                <td class="text-right" :class="farkColor(rapor.Ozet.ToplamFark)">{{ rapor.Ozet.ToplamFarkStr }}</td>
                                <td></td>
                                <td class="text-right">{{ rapor.Ozet.ToplamHakedisStr }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <div v-if="rapor && rapor.Nakliyeciler.length === 0" class="text-center py-4 text-muted">
            <feather-icon icon="InfoIcon"></feather-icon>
            Bu filtreye uygun veri bulunamadı.
        </div>
    </b-card>
</template>

<script>
import { BCard, BButton, BBadge } from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import { IrsaliyeOperationsApi } from '@/api/irsaliye'
import { NetsisApi } from '@/api/netsis'
import VStatsBox from '@/components/VStatsBox.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import moment from 'moment'

export default {
    components:{
        BCard, BButton, BBadge, VueApexCharts, VStatsBox, FeatherIcon
    },
    data(){
        return {
            queryData:{
                // Varsayılan: son 1 ay. Adamın Excel'inde hafta bazlı takip var, ay uygun bir ilk pencere.
                StartDate: moment().subtract(1,'months').startOf('month').format('YYYY-MM-DD'),
                EndDate:   moment().endOf('month').format('YYYY-MM-DD'),
                CariCode:  null,
                BranchCode: null
            },
            cariList: [],
            cariLoading: false,
            subeList: [],
            rapor: null,
            loading: false,
            excelLoading: false,
            // Ağaç durumu: hangi düğüm açık? key = 'n-0', 'd-0-1', 'g-0-1-2'
            openMap: {}
        }
    },
    mounted(){
        this.getSubeList()
        this.loadReport()
    },
    methods:{
        getSubeList(){
            NetsisApi.GetSubeList().then(res => {
                if(res?.data?.Success) this.subeList = res.data.Data || []
            }).catch(() => {})
        },
        searchCari(searchKey){
            if(!searchKey) return
            this.cariLoading = true
            NetsisApi.CariList(searchKey).then(res => {
                this.cariList = res?.data?.Data || []
                this.cariLoading = false
            }).catch(() => { this.cariLoading = false })
        },
        loadReport(){
            this.loading = true
            IrsaliyeOperationsApi.NakliyeciHakedisRaporu(this.queryData).then(res => {
                if(res?.data?.Success){
                    this.rapor = res.data.Data
                    // Yeni rapor gelince ağaç kapansın.
                    this.openMap = {}
                }else{
                    this.$result(res)
                    this.rapor = null
                }
                this.loading = false
            }).catch(err => {
                this.$result(null, err)
                this.loading = false
            })
        },
        downloadExcel(){
            this.excelLoading = true
            IrsaliyeOperationsApi.NakliyeciHakedisRaporuExcel(this.queryData).then(res => {
                // Blob'u indir
                const url = URL.createObjectURL(res.data)
                const a = document.createElement('a')
                a.href = url
                a.download = `nakliyeci-hakedis-raporu-${moment().format('YYYYMMDD-HHmm')}.xlsx`
                document.body.appendChild(a)
                a.click()
                a.remove()
                URL.revokeObjectURL(url)
                this.excelLoading = false
            }).catch(err => {
                this.$result(null, err)
                this.excelLoading = false
            })
        },
        toggle(key){ this.$set(this.openMap, key, !this.openMap[key]) },
        isOpen(key){ return !!this.openMap[key] },
        farkColor(v){
            if(v == null || v === 0) return ''
            return v > 0 ? 'text-warning' : 'text-danger'
        }
    },
    computed:{
        date:{
            get(){ return [this.queryData.StartDate, this.queryData.EndDate].filter(d => d) },
            set(v){ this.queryData.StartDate = v[0] || null; this.queryData.EndDate = v[1] || null }
        },
        cari:{
            get(){ return this.cariList.find(c => c.CARI_KOD === this.queryData.CariCode) },
            set(v){ this.queryData.CariCode = v?.CARI_KOD || null }
        },
        sube:{
            get(){ return this.subeList.find(s => s.BranchCode === this.queryData.BranchCode) },
            set(v){ this.queryData.BranchCode = v?.BranchCode || null }
        },
        farkVariant(){
            if(!this.rapor) return 'secondary'
            const f = this.rapor.Ozet.ToplamFark
            if(!f || Math.abs(f) < 0.01) return 'secondary'
            return f > 0 ? 'warning' : 'danger'
        },
        donutSeries(){
            if(!this.rapor?.Urunler) return []
            return this.rapor.Urunler.map(u => Number(u.Hakedis) || 0)
        },
        donutOptions(){
            const labels = (this.rapor?.Urunler || []).map(u => u.UrunAdi)
            return {
                chart: { type: 'donut' },
                labels,
                legend: { position: 'bottom' },
                tooltip: {
                    y: {
                        formatter: v => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v) + ' ₺'
                    }
                },
                dataLabels: { enabled: true, formatter: v => v.toFixed(1) + '%' }
            }
        },
        lineSeries(){
            if(!this.rapor?.Gunluk) return []
            return [
                {
                    name: 'Tonaj',
                    data: this.rapor.Gunluk.map(g => Number(g.Tonaj) || 0)
                },
                {
                    name: 'Hakediş (÷100)',
                    // Tonaj ve hakediş çok farklı büyüklükte; hakedişi 100'e böl ki aynı grafik üzerinde okunabilsin.
                    data: this.rapor.Gunluk.map(g => Math.round((Number(g.Hakedis) || 0) / 100))
                }
            ]
        },
        lineOptions(){
            const cats = (this.rapor?.Gunluk || []).map(g => g.GunStr)
            return {
                chart: { type: 'area', toolbar: { show: false } },
                xaxis: { categories: cats },
                stroke: { curve: 'smooth', width: 2 },
                dataLabels: { enabled: false },
                legend: { position: 'bottom' }
            }
        }
    }
}
</script>

<style scoped>
.pl-3 { padding-left: 1.5rem !important; }
.pl-4 { padding-left: 2.5rem !important; }
.pl-5 { padding-left: 3.5rem !important; }
</style>
